import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { PaymentMethodResponseDTO } from '@/core/api/dtos/response/payment/paymentMethod'

import apiClient from '@core/api/client'
import { CreateCardPaymentIntentRequestDTO } from '@core/api/dtos/request/payment/createCardPaymentIntent'
import { CreatePaymentTokenRequestDTO } from '@core/api/dtos/request/payment/createPaymentToken'
import { CreateSepaPaymentIntentRequestDTO } from '@core/api/dtos/request/payment/createSepaPaymentIntent'
import { UpdatePaymentTokenRequestDTO } from '@core/api/dtos/request/payment/updatePaymentToken'
import { PaymentIntentResponseDTO } from '@core/api/dtos/response/payment/paymentIntent'

export const paymentService = {
	/**
	 * Get available payment methods for the user
	 */
	getAvailablePaymentMethods(
		config?: AxiosRequestConfig
	): Promise<AxiosResponse<PaginatedResponse<PaymentMethodResponseDTO>>> {
		return apiClient.get('/payments/payment_methods/available', config)
	},

	/**
	 * Create a card payment intent
	 */
	createCardPaymentIntent(
		data: CreateCardPaymentIntentRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse<{ data: PaymentIntentResponseDTO }>> {
		return apiClient.post('/payments/create-card-payment-intent', data, config)
	},

	/**
	 * Create a SEPA payment intent
	 */
	createSepaPaymentIntent(
		data: CreateSepaPaymentIntentRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse<{ data: PaymentIntentResponseDTO }>> {
		return apiClient.post('/payments/create-sepa-payment-intent', data, config)
	},

	/**
	 * Create a payment token from payment intent
	 */
	createPaymentToken(
		data: CreatePaymentTokenRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post('/payments/create-payment-token', data, config)
	},

	/**
	 * Get payment tokens for the account
	 */
	getPaymentTokens(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/payment_tokens?with_payment_info=true', config)
	},

	/**
	 * Update a payment token
	 */
	patchPaymentToken(
		paymentTokenId: number,
		data: UpdatePaymentTokenRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.patch(`/my/payment_tokens/${paymentTokenId}`, data, config)
	},

	/**
	 * Delete a payment token
	 */
	deletePaymentToken(
		paymentTokenId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/payment_tokens/${paymentTokenId}`, config)
	},

	/**
	 * Alegra endpoints for location data
	 */
	// CHL
	getAlegraRegions(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/regions', config)
	},
	getAlegraCommunes(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/communes', config)
	},
	// ARG
	getAlegraProvinces(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/provinces', config)
	},
	getAlegraCities(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/cities', config)
	},
	// ESP
	getAlegraEsProvinces(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/es_provinces', config)
	},
	getAlegraEsCities(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/es_cities', config)
	},
	// COL
	getAlegraCoDepartments(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/co_departments', config)
	},
	getAlegraCoCities(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/co_cities', config)
	},
	getAlegraCoZipCodes(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/payments/alegra/co_zip_codes', config)
	},
}
